import { useEffect, useRef, useState } from 'react';

// A slowly turning dot-matrix globe with Abuja pinned on it.
const ABUJA = { lat: 9.0765, lon: 7.3986 };
const SIZE = 196;
const R = 78;
const TILT = (-14 * Math.PI) / 180;
const SHADES = '.·:-=+*';

function cssVar(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
}

// Rotate a lat/lon point by `spin` around the polar axis, tilt it, and project.
function project(lat, lon, spin) {
    const phi = (lat * Math.PI) / 180;
    const lam = (lon * Math.PI) / 180 + spin;
    const x = Math.cos(phi) * Math.sin(lam);
    const y0 = Math.sin(phi);
    const z0 = Math.cos(phi) * Math.cos(lam);
    const y = y0 * Math.cos(TILT) - z0 * Math.sin(TILT);
    const z = y0 * Math.sin(TILT) + z0 * Math.cos(TILT);
    return { x: SIZE / 2 + x * R, y: SIZE / 2 - y * R, z };
}

export default function Globe() {
    const ref = useRef(null);
    const [time, setTime] = useState('');

    useEffect(() => {
        const fmt = () =>
            new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date());
        setTime(fmt());
        const id = setInterval(() => setTime(fmt()), 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const canvas = ref.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return undefined;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = SIZE * dpr;
        canvas.height = SIZE * dpr;
        ctx.scale(dpr, dpr);
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let spin = -0.6;
        let raf = 0;
        let last = 0;

        const draw = (now) => {
            raf = requestAnimationFrame(draw);
            if (now - last < 50) return; // ~20fps is plenty for a slow spin
            last = now;
            if (!reduced) spin += 0.006;
            const ink = cssVar('ink');
            const dim = cssVar('dim');
            const accent = cssVar('accent');
            ctx.clearRect(0, 0, SIZE, SIZE);
            ctx.font = '9px "IBM Plex Mono", monospace';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            for (let lat = -80; lat <= 80; lat += 10) {
                const ring = Math.max(1, Math.round(36 * Math.cos((lat * Math.PI) / 180)));
                for (let k = 0; k < ring; k++) {
                    const p = project(lat, (k * 360) / ring, spin);
                    if (p.z <= 0) continue;
                    ctx.globalAlpha = 0.15 + p.z * 0.6;
                    ctx.fillStyle = lat === 0 ? ink : dim;
                    ctx.fillText(SHADES[Math.min(SHADES.length - 1, Math.floor(p.z * SHADES.length))], p.x, p.y);
                }
            }

            // Outline.
            ctx.globalAlpha = 0.35;
            ctx.strokeStyle = dim;
            ctx.beginPath();
            ctx.arc(SIZE / 2, SIZE / 2, R + 6, 0, Math.PI * 2);
            ctx.stroke();

            // Abuja pin with a leader line out to the edge.
            const a = project(ABUJA.lat, ABUJA.lon, spin);
            if (a.z > 0) {
                ctx.globalAlpha = 1;
                ctx.fillStyle = accent;
                ctx.fillRect(a.x - 3, a.y - 3, 6, 6);
                ctx.strokeStyle = accent;
                ctx.globalAlpha = 0.6;
                ctx.setLineDash([2, 3]);
                ctx.beginPath();
                ctx.moveTo(a.x + 4, a.y + 4);
                ctx.lineTo(SIZE - 8, SIZE - 8);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 0.5 + 0.5 * Math.abs(Math.sin(now / 400));
                ctx.strokeRect(a.x - 7, a.y - 7, 14, 14);
            }
            ctx.globalAlpha = 1;
        };
        raf = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(raf);
    }, []);

    return (
        <aside className="globe" aria-label="Location: Abuja, Nigeria">
            <canvas ref={ref} style={{ width: SIZE, height: SIZE }} aria-hidden="true" />
            <p className="globe-read">
                <span className="globe-k">['abuja']</span> {ABUJA.lat.toFixed(4)}°N {ABUJA.lon.toFixed(4)}°E
                <br />
                <span className="globe-k">['local']</span> {time} WAT
                <br />
                <span className="globe-k">['offset']</span> UTC+01:00
            </p>
        </aside>
    );
}
